import { useState } from "react";
import { Check, Lightbulb, TrendingUp, AlertCircle, Sparkles, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AdjustmentConfirmDialog } from "./AdjustmentConfirmDialog";

interface AICoachInsightsProps {
  insights: {
    summary: string;
    strengths: string[];
    improvements: string[];
    recommendations: string[];
    adjustments: {
      difficulty: 'increase' | 'maintain' | 'decrease';
      pace: 'faster' | 'maintain' | 'slower';
      focusChange: boolean;
      reasoning: string;
    };
    motivation: string;
  };
  metrics: {
    completionRate: number;
    accuracyTrend: 'improving' | 'stable' | 'declining';
    paceStatus: 'ahead' | 'on-track' | 'behind';
  };
  weekNumber: number;
  planId?: string;
  weekId?: string;
  insightId?: string;
  canApply?: boolean;
  onApplySuccess?: () => void;
}

export function AICoachInsights({ 
  insights, 
  metrics, 
  weekNumber, 
  planId, 
  weekId, 
  insightId, 
  canApply = false,
  onApplySuccess,
}: AICoachInsightsProps) {
  const [isApplying, setIsApplying] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [applyToFuture, setApplyToFuture] = useState(true);

  const handleApplyAdjustments = async (applyToAllFuture: boolean) => {
    setApplyToFuture(applyToAllFuture);
    setShowConfirmDialog(true);
  };

  const confirmApplyAdjustments = async () => {
    if (!planId || !weekId || !insightId) return;

    setIsApplying(true);
    try {
      const { data, error } = await supabase.functions.invoke("apply-plan-adjustments", {
        body: {
          planId,
          weekId,
          insightId,
          adjustments: insights.adjustments,
          applyToFutureWeeks: applyToFuture,
        },
      });

      if (error) throw error;

      toast.success(
        `Applied adjustments to ${data.weeksUpdated} week(s) and ${data.tasksUpdated} task(s)`
      );
      setShowConfirmDialog(false);
      if (onApplySuccess) onApplySuccess();
    } catch (error: any) {
      console.error("Error applying adjustments:", error);
      toast.error(error.message || "Failed to apply adjustments");
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      <Card className="border-l-4 border-l-primary">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <CardTitle>Weekly AI Coach Insights</CardTitle>
          <Badge variant="secondary">Week {weekNumber}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary */}
        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="text-sm">{insights.summary}</p>
        </div>
        
        {/* Strengths */}
        <div>
          <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-green-600" />
            Key Strengths
          </h4>
          <ul className="space-y-1">
            {insights.strengths.map((s, i) => (
              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Improvements */}
        {insights.improvements.length > 0 && (
          <div>
            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              Areas for Improvement
            </h4>
            <ul className="space-y-1">
              {insights.improvements.map((imp, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-orange-600 mt-0.5 flex-shrink-0">•</span>
                  <span>{imp}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Recommendations */}
        <div>
          <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-yellow-600" />
            This Week's Focus
          </h4>
          <ul className="space-y-1">
            {insights.recommendations.map((r, i) => (
              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                <span className="text-primary mt-0.5 flex-shrink-0">→</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Adjustments */}
        {(insights.adjustments.focusChange || 
          insights.adjustments.difficulty !== 'maintain' || 
          insights.adjustments.pace !== 'maintain') && (
          <div className="p-4 border border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-900 rounded-lg">
            <h4 className="font-semibold text-sm mb-2 text-orange-900 dark:text-orange-100">
              Suggested Plan Adjustment
            </h4>
            <p className="text-sm text-orange-800 dark:text-orange-200 mb-4">
              {insights.adjustments.reasoning}
            </p>

            {/* Adjustment Details */}
            <div className="space-y-2 mb-4">
              {insights.adjustments.difficulty !== 'maintain' && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-orange-800 dark:text-orange-200">Difficulty:</span>
                  <Badge variant="outline">
                    {insights.adjustments.difficulty === 'increase' ? '↑ Increase' : '↓ Decrease'}
                  </Badge>
                </div>
              )}
              {insights.adjustments.pace !== 'maintain' && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-orange-800 dark:text-orange-200">Pace:</span>
                  <Badge variant="outline">
                    {insights.adjustments.pace === 'faster' ? '⚡ Speed up' : '🐢 Slow down'}
                  </Badge>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {canApply && (
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  onClick={() => handleApplyAdjustments(true)}
                  disabled={isApplying}
                  className="flex-1"
                >
                  {isApplying ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Apply to All Future Weeks
                    </>
                  )}
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleApplyAdjustments(false)}
                  disabled={isApplying}
                >
                  This Week Only
                </Button>
              </div>
            )}
          </div>
        )}
        
        {/* Motivation */}
        <div className="p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/20">
          <p className="text-sm font-medium text-primary">
            💪 {insights.motivation}
          </p>
        </div>
      </CardContent>
    </Card>

    <AdjustmentConfirmDialog
      open={showConfirmDialog}
      onOpenChange={setShowConfirmDialog}
      adjustments={insights.adjustments}
      applyToFuture={applyToFuture}
      affectedWeeks={applyToFuture ? 4 : 1}
      affectedTasks={applyToFuture ? 20 : 5}
      onConfirm={confirmApplyAdjustments}
      onCancel={() => setShowConfirmDialog(false)}
      isApplying={isApplying}
    />
    </>
  );
}
